import { Link } from 'react-router-dom';
import { Lock, ShieldCheck, Server, ExternalLink, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl, urlDomain } from '@/lib/sanitizeUrl';
import type { CrewCoordinator, SkateCrew } from '@/lib/types';
import { nip19 } from 'nostr-tools';

/**
 * Shows the crew's encrypted-chat coordinator (Cordn / MLS Delivery Service).
 *
 * Every crew already has built-in end-to-end encrypted group chat (NIP-17
 * giftwrap to the roster) — no setup required. This card:
 *   - by default, reassures the crew that private chat is already ON; and
 *   - if a founder has opted into routing chat through their own self-hosted
 *     Cordn coordinator, surfaces which coordinator is in use.
 *
 * Note: the coordinator path currently provides NIP-44 group encryption routed
 * over ContextVM (nothing crew-specific hits a public relay). It is NOT yet a
 * full MLS ratchet, so we deliberately avoid claiming forward secrecy here.
 */
export function CrewCoordinatorCard({
  crew,
  isFounder,
}: {
  crew: SkateCrew;
  isFounder: boolean;
}) {
  const { coordinator } = crew;

  if (!coordinator) {
    // Default state: private chat is ALREADY on for every crew (built-in NIP-17
    // giftwrap group chat, no setup). We say so plainly rather than implying a
    // coordinator is required. Founders get a small, honestly-labeled link to
    // the experimental self-hosted-coordinator option.
    return (
      <Card className="border-2 border-teal-500/25 bg-gradient-to-br from-teal-500/[0.06] to-black/90">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg font-black uppercase text-white">
            <Lock className="h-5 w-5 text-teal-400" /> Private Crew Chat
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-teal-500/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-teal-300">
              <ShieldCheck className="h-3 w-3" /> On
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm leading-relaxed text-gray-400">
            The <span className="font-semibold text-gray-200">Chat</span> tab is{' '}
            <span className="font-bold text-teal-300">end-to-end encrypted</span> for the founder and all
            listed members — nothing to set up. Messages are sealed to each member's key (NIP-17), so only
            your crew can read them.
          </p>
          {isFounder && (
            <p className="text-[11px] leading-relaxed text-gray-600">
              Advanced: run your own{' '}
              <a
                href="https://github.com/Cordn-msg/cordn"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 underline underline-offset-2 hover:text-teal-300"
              >
                Cordn coordinator
              </a>
              ?{' '}
              <Link
                to={`/edit-crew/${crew.pubkey}/${crew.identifier}`}
                className="inline-flex items-center gap-1 font-semibold text-gray-400 hover:text-teal-300"
              >
                <Settings className="h-3 w-3" /> Route chat through it
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] to-black/90">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-black uppercase text-white">
          <Lock className="h-5 w-5 text-emerald-400" /> Private Crew Chat
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-emerald-300">
            <Server className="h-3 w-3" /> Coordinator
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <CoordinatorEndpoint coordinator={coordinator} />

        {coordinator.operator && <CoordinatorOperator pubkey={coordinator.operator} />}

        <div className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
          <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-300">
            <Lock className="h-3.5 w-3.5" /> Private chat is live
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
            The <span className="font-semibold text-gray-300">Chat</span> tab runs through this
            coordinator: messages are end-to-end encrypted (NIP-44, sealed to each member) and routed
            over ContextVM, so the coordinator only ever handles sealed traffic and nothing
            crew-specific hits a public relay.
          </p>
        </div>

        {isFounder && (
          <Link
            to={`/edit-crew/${crew.pubkey}/${crew.identifier}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 hover:text-emerald-300"
          >
            <Settings className="h-3.5 w-3.5" /> Manage coordinator
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function CoordinatorEndpoint({ coordinator }: { coordinator: CrewCoordinator }) {
  const safe = sanitizeUrl(coordinator.url);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-black/40 p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15">
        <Server className="h-5 w-5 text-emerald-400" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400/80">
          Coordinator
        </p>
        <p className="truncate text-sm font-bold text-white">{urlDomain(coordinator.url)}</p>
      </div>
      {safe && (
        <a
          href={safe}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-gray-400 transition-colors hover:border-emerald-500/40 hover:text-emerald-300"
          title={`Open ${urlDomain(coordinator.url)}`}
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      )}
    </div>
  );
}

function CoordinatorOperator({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const name = author.data?.metadata?.name ?? genUserName(pubkey);
  const pic = author.data?.metadata?.picture;
  const npub = nip19.npubEncode(pubkey);

  return (
    <Link
      to={`/${npub}`}
      className="flex items-center gap-2.5 rounded-xl bg-black/40 p-2.5 transition-colors hover:bg-black/60"
    >
      <Avatar className="h-8 w-8">
        <AvatarImage src={pic} alt={name} />
        <AvatarFallback className="text-[10px]">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Coordinator run by</p>
        <p className="truncate text-sm font-bold text-gray-200">{name}</p>
      </div>
    </Link>
  );
}
